import type { ReactNode } from 'react'
import styles from './SectionList.module.css'

export function PageShell({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className={styles.page}>
      <h1 className={styles.pageTitle}>{title}</h1>
      {children}
    </div>
  )
}

export function SectionList({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <section className={styles.section}>
      {title && <h2 className={styles.sectionTitle}>{title}</h2>}
      <div className={styles.card}>{children}</div>
    </section>
  )
}

export function Row({
  label,
  value,
  onClick,
  trailing,
  destructive,
  disabled,
}: {
  label: string
  value?: ReactNode
  onClick?: () => void
  trailing?: ReactNode
  destructive?: boolean
  disabled?: boolean
}) {
  const cls = `${styles.row}${onClick ? '' : ' ' + styles.rowStatic}${destructive ? ' ' + styles.destructive : ''}`
  const content = (
    <>
      <span className={styles.label}>{label}</span>
      {value != null && <span className={styles.value}>{value}</span>}
      {trailing}
    </>
  )
  if (onClick) {
    return <button type="button" className={cls} onClick={onClick} disabled={disabled}>{content}</button>
  }
  return <div className={cls}>{content}</div>
}

/** Stacked row: label above a full-width control (inputs that need room, e.g. a password). */
export function FieldRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={`${styles.row} ${styles.rowStatic} ${styles.fieldRow}`}>
      <span className={styles.label}>{label}</span>
      {children}
    </div>
  )
}

/** Helper text under a row group; `status` announces feedback to screen readers. */
export function Note({ children, status }: { children: ReactNode; status?: boolean }) {
  return <p className={styles.note} role={status ? 'status' : undefined}>{children}</p>
}

/** Segmented single-choice control (radiogroup) with 44px targets. */
export function Segmented<T extends string>({
  label, value, options, onChange,
}: {
  label: string
  value: T
  options: { value: T; label: string }[]
  onChange: (v: T) => void
}) {
  return (
    <div className={styles.segmented} role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          className={`${styles.segment}${value === o.value ? ' ' + styles.segmentOn : ''}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}
